import { useBlocker } from 'react-router-dom'
import { useBeforeUnloadWarning } from '@/hooks/useBeforeUnloadWarning'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import { useTranslation } from '@/i18n'

interface UnsavedChangesGuardProps {
  /** True when the editor has edits that haven't been persisted (Save Changes). */
  isModified: boolean
  /** Persists the graph; returns false on failure. Awaited before leaving. */
  onSave: () => Promise<boolean> | void
}

/**
 * Stops unsaved annotations from vanishing when the user leaves the editor.
 * The V2 layout renders outside BoardLayout and so lost the guards BoardLayout
 * provided. This restores them with one mechanism:
 *
 * - `useBlocker` intercepts every in-app navigation — the breadcrumb, the
 *   sidebar, and the browser Back/Forward buttons (SPA history navigations that
 *   `beforeunload` never sees) — and shows a Save / Discard / Cancel modal.
 * - `useBeforeUnloadWarning` covers the cases the router can't: closing or
 *   refreshing the tab, where the browser only permits a native warning.
 */
export default function UnsavedChangesGuard({ isModified, onSave }: UnsavedChangesGuardProps) {
  const { t } = useTranslation()

  useBeforeUnloadWarning(isModified)

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isModified && currentLocation.pathname !== nextLocation.pathname,
  )

  const isBlocked = blocker.state === 'blocked'

  const handleSaveAndLeave = async () => {
    const result = await onSave()
    // onSave surfaces its own error toast on failure; stay blocked so the user
    // can retry or cancel instead of losing the work.
    if (result === false) return
    blocker.proceed?.()
  }

  const handleDiscardAndLeave = () => blocker.proceed?.()
  const handleStay = () => blocker.reset?.()

  return (
    <AlertDialog open={isBlocked} onOpenChange={(open) => { if (!open) handleStay() }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('data_catalog:graph_editor.unsaved_modal.title', { defaultValue: 'Unsaved changes' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('data_catalog:graph_editor.unsaved_modal.description', {
              defaultValue: 'You have unsaved changes. Save them before leaving, or discard them?',
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleStay}>
            {t('common:buttons.cancel', { defaultValue: 'Cancel' })}
          </AlertDialogCancel>
          <Button variant="outline" onClick={handleDiscardAndLeave}>
            {t('data_catalog:graph_editor.unsaved_modal.discard', { defaultValue: 'Discard changes' })}
          </Button>
          <Button onClick={handleSaveAndLeave}>
            {t('data_catalog:graph_editor.unsaved_modal.save_and_leave', { defaultValue: 'Save & leave' })}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
